/**
 * V2DesignSystemPage — a living token & component reference for the V2
 * interface, faithful to design_reference/js/screens-misc.jsx `DesignSystem`.
 *
 * Sections: canvas ramp · text ramp · accent · semantic + source colors ·
 * type specimen · controls · pills/badges/code-cells · states (skeleton +
 * empty). Everything is bound to V2 tokens (`var(--bg-2)` etc.) and the live
 * accent from {@link useUiStore}, so the page reflects the active theme.
 */

import { useUiStore } from '../theme/uiStore';
import { SOURCE_META, CONF_META } from '../lib/semantics';
import { Button } from '../components/ui/button';
import { Segmented } from '../components/ui/segmented';
import { Chip } from '../components/ui/chip';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Pill } from '../components/ui/pill';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { CodeCell } from '../components/CodeCell';
import {
  IconPlus,
  IconDownload,
  IconList,
  IconCards,
  IconTag,
  IconLayers,
  IconClipboard,
} from '../components/icons';

/** A single token swatch (color chip + label + var name). */
function Swatch({ label, varname, big }: { label: string; varname: string; big?: boolean }) {
  return (
    <div className="flex flex-col gap-[5px]">
      <div
        className="rounded-[var(--r-md)] border border-[var(--bd)] shadow-[inset_0_1px_0_var(--hi-top)]"
        style={{ height: big ? 52 : 40, background: `var(${varname})` }}
      />
      <div className="text-[10.5px] text-[var(--tx-mid)]">
        {label}
        <div className="font-[var(--font-mono)] text-[9.5px] text-[var(--tx-faint)]">
          {varname}
        </div>
      </div>
    </div>
  );
}

/** A titled reference block (uppercase heading + optional sub + content). */
function DSBlock({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-[26px]">
      <div className="mb-3">
        <h2 className="m-0 text-[13px] font-[650] uppercase tracking-[.04em] text-[var(--tx-lo)]">
          {title}
        </h2>
        {sub && <p className="m-0 mt-[3px] text-[12px] text-[var(--tx-faint)]">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

/** A bordered "card" container matching the prototype's `.card`. */
function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={
        'rounded-[var(--r-lg)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] ' +
        'shadow-[inset_0_1px_0_var(--hi-top)] ' +
        (className ?? '')
      }
    >
      {children}
    </div>
  );
}

/** The V2 Design System reference page. */
export default function V2DesignSystemPage() {
  const accent = useUiStore((s) => s.accent);

  return (
    <div className="mx-auto max-w-[1080px] p-5">
      {/* Page head */}
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="m-0 mb-[3px] text-[21px] font-[640] tracking-[-0.03em] text-[var(--tx-hi)]">
            Design System
          </h1>
          <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">
            Tokens and components for the V2 interface. Dark-first, 4px grid,
            Linear-grade depth.
          </p>
        </div>
        <Pill kind="accent">
          <IconLayers size={12} />
          v2.0 tokens
        </Pill>
      </div>

      {/* Canvas ramp */}
      <DSBlock
        title="Canvas · graphite ramp"
        sub="Midnight-graphite, cool undertone — not pure black."
      >
        <div className="grid grid-cols-6 gap-[10px]">
          <Swatch label="App" varname="--bg-0" />
          <Swatch label="Chrome" varname="--bg-1" />
          <Swatch label="Panel" varname="--bg-2" />
          <Swatch label="Row" varname="--bg-3" />
          <Swatch label="Hover" varname="--bg-4" />
          <Swatch label="Inset" varname="--bg-inset" />
        </div>
      </DSBlock>

      {/* Text ramp */}
      <DSBlock title="Text ramp" sub="Four levels of foreground emphasis on the dark canvas.">
        <div className="grid grid-cols-4 gap-[10px]">
          <Swatch label="High" varname="--tx-hi" />
          <Swatch label="Mid" varname="--tx-mid" />
          <Swatch label="Low" varname="--tx-lo" />
          <Swatch label="Faint" varname="--tx-faint" />
        </div>
      </DSBlock>

      {/* Accent */}
      <DSBlock
        title="Signature accent"
        sub={`Current accent: ${accent}. Swap the accent (lime / blue / cyan) and theme live in the Tweaks panel.`}
      >
        <div className="grid grid-cols-4 gap-[10px]">
          <Swatch label="Accent" varname="--accent" big />
          <Swatch label="Accent hi" varname="--accent-hi" big />
          <Swatch label="Accent ink (text)" varname="--accent-ink" big />
          <Swatch label="Accent soft" varname="--accent-soft" big />
        </div>
        <div className="mt-2 text-[11px] text-[var(--accent-ink)]">
          Accent text reads on dark via <code>var(--accent-ink)</code>.
        </div>
      </DSBlock>

      {/* Semantic + source colors */}
      <DSBlock
        title="Semantic & source colors"
        sub="Functional color carried by data: status, confidence, and line-item source."
      >
        <div className="mb-3.5 grid grid-cols-6 gap-[10px]">
          <Swatch label="Success" varname="--ok" />
          <Swatch label="Warning" varname="--warn" />
          <Swatch label="Danger" varname="--bad" />
          <Swatch label="Info" varname="--info" />
          <Swatch label="Rules (violet)" varname="--violet" />
          <Swatch label="Companion (teal)" varname="--teal" />
        </div>
        <div className="flex flex-wrap gap-4">
          {Object.entries(SOURCE_META).map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-[6px] text-[11px] text-[var(--tx-lo)]"
            >
              <span
                className="h-[7px] w-[7px] flex-none rounded-full"
                style={{ background: v.color }}
                aria-hidden="true"
              />
              {v.label}
            </span>
          ))}
        </div>
      </DSBlock>

      {/* Type specimen */}
      <DSBlock
        title="Type"
        sub="Inter Tight for UI · JetBrains Mono for codes and numeric columns."
      >
        <Card className="p-[18px]">
          <div className="text-[28px] font-[650] tracking-[-0.03em] text-[var(--tx-hi)]">
            Defensible estimates, a generation newer
          </div>
          <div className="mt-1.5 text-[15px] text-[var(--tx-mid)]">
            Body / UI — Inter Tight, optimized for dense data tables and tree
            navigation.
          </div>
          <div className="mt-2.5 text-[13px] text-[var(--tx-lo)]">
            Small / meta — labels, captions, and secondary information.
          </div>
          <div className="mt-2.5 font-[var(--font-mono)] text-[14px] text-[var(--accent-ink)]">
            RFG&nbsp;240S&nbsp;&nbsp;·&nbsp;&nbsp;$37,461.94&nbsp;&nbsp;·&nbsp;&nbsp;24.62 SQ
          </div>
        </Card>
      </DSBlock>

      {/* Controls */}
      <DSBlock title="Buttons & controls">
        <Card className="flex flex-wrap items-center gap-3 p-[18px]">
          <Button variant="primary">
            <IconPlus />
            Primary
          </Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="secondary" size="sm">
            <IconDownload />
            Small
          </Button>
          <Segmented<'list' | 'cards'>
            value="list"
            onValueChange={() => {}}
            options={[
              { value: 'list', label: 'List', icon: <IconList /> },
              { value: 'cards', label: 'Cards', icon: <IconCards /> },
            ]}
          />
          <div className="flex gap-1.5">
            <Chip on count={12}>
              All
            </Chip>
            <Chip count={4}>Web</Chip>
          </div>
          <Switch defaultChecked />
          <Switch />
          <Checkbox defaultChecked />
          <Checkbox />
        </Card>
      </DSBlock>

      {/* Pills, badges & code cells */}
      <DSBlock title="Pills, badges & code cells">
        <Card className="flex flex-wrap items-center gap-2.5 p-[18px]">
          <Pill kind="ok" dot="var(--ok)">
            Active
          </Pill>
          <Pill kind="warn">{CONF_META.medium.label}</Pill>
          <Pill kind="bad">{CONF_META.low.label}</Pill>
          <Pill kind="info">Water</Pill>
          <Pill kind="neutral">Neutral</Pill>
          <Pill kind="accent">
            <IconTag size={11} />
            CLM-FL-77421
          </Pill>
          <div className="flex items-center gap-2">
            <CodeCell value="RFG" field="cat" onCommit={() => {}} />
            <CodeCell value="240S" field="sel" onCommit={() => {}} />
          </div>
          <span className="grid h-[22px] w-[22px] place-items-center rounded-[5px] border border-[var(--bd-subtle)] bg-[var(--bg-3)] font-[var(--font-mono)] text-[12px] text-[var(--tx-mid)]">
            &amp;
          </span>
        </Card>
      </DSBlock>

      {/* States */}
      <DSBlock
        title="States"
        sub="Loading skeletons and empty states keep the dense UI calm while data resolves."
      >
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4">
            {[80, 60, 70].map((w, i) => (
              <Skeleton key={i} className="mb-2.5 h-3" style={{ width: `${w}%` }} />
            ))}
            <Skeleton className="mt-1.5 h-7" style={{ width: '40%' }} />
          </Card>
          <Card>
            <EmptyState
              icon={<IconClipboard />}
              title="No estimates yet"
              description="Upload a PDF or build a scope to get started."
            />
          </Card>
        </div>
      </DSBlock>
    </div>
  );
}
